import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack,
    TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthService } from '../../services/auth.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { QcInspectionService } from '../../services/qcInspection.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';
import { IBuyerOrder } from '../../interfaces/garments/buyerOrder.interface';
import {
    INSPECTION_LEVELS, IQcDefect, IQcInspection, IQcPlanPreview, InspectionLevel, QC_TYPE_LABELS,
    QcInspectionType, SEVERITY_LABELS, SUPPORTED_AQLS, DefectSeverity,
} from '../../interfaces/garments/qcInspection.interface';
import { toLocalIsoDate } from '../../utils/dateUtils';

const TYPES = Object.keys(QC_TYPE_LABELS) as QcInspectionType[];

/** Shows the numbers the server will apply, from the same endpoint that books the inspection. */
export function PlanPreviewLine({ plan }: { plan: IQcPlanPreview | null }) {
    if (!plan) return null;
    return (
        <Alert severity="info" data-testid="qc-plan-preview" data-help="merchandising.qc.planPreview">
            Code letter {plan.codeLetter}. {plan.fullInspection ? 'Inspect every piece' : `Inspect ${plan.sampleSize} pieces`}.
            {' '}Critical: accept 0. Major: accept up to {plan.majorAccept}, reject at {plan.majorReject}.
            {' '}Minor: accept up to {plan.minorAccept}, reject at {plan.minorReject}.
        </Alert>
    );
}

interface BookProps {
    open: boolean;
    onClose: () => void;
    onSaved: (q: IQcInspection) => void;
    /** When set, the order is fixed (booking from the buyer order page). */
    fixedOrderId?: string;
    editing?: IQcInspection | null;
}

export function QcBookDialog({ open, onClose, onSaved, fixedOrderId, editing }: BookProps) {
    const toast = useToast();
    const [orders, setOrders] = useState<IBuyerOrder[]>([]);
    const [orderId, setOrderId] = useState('');
    const [type, setType] = useState<QcInspectionType>('FINAL');
    const [date, setDate] = useState(toLocalIsoDate(new Date()));
    const [lot, setLot] = useState('');
    const [level, setLevel] = useState<InspectionLevel>('II');
    const [aqlMajor, setAqlMajor] = useState('2.5');
    const [aqlMinor, setAqlMinor] = useState('4.0');
    const [inspector, setInspector] = useState('');
    const [plan, setPlan] = useState<IQcPlanPreview | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        if (editing) {
            setOrderId(editing.buyerOrderId); setType(editing.inspectionType); setDate(editing.scheduledDate);
            setLot(String(editing.lotSize)); setLevel(editing.inspectionLevel);
            setAqlMajor(SUPPORTED_AQLS.find(a => Number(a) === Number(editing.aqlMajor)) ?? '2.5');
            setAqlMinor(SUPPORTED_AQLS.find(a => Number(a) === Number(editing.aqlMinor)) ?? '4.0');
            setInspector(editing.inspectorName ?? '');
        } else {
            setOrderId(fixedOrderId ?? ''); setLot(''); setInspector('');
        }
        if (!fixedOrderId) {
            BuyerOrderService.list(inst, 0, 200)
                .then(p => setOrders(p.content))
                .catch(e => toast.showApiError(e, 'Failed to load orders'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fixedOrderId, editing]);

    useEffect(() => {
        const inst = AuthService.getSelectedInstitution();
        const n = Number(lot);
        if (!open || !inst || !Number.isInteger(n) || n < 1) { setPlan(null); return; }
        let cancelled = false;
        QcInspectionService.previewPlan(inst, n, level, Number(aqlMajor), Number(aqlMinor))
            .then(p => { if (!cancelled) setPlan(p); })
            .catch(() => { if (!cancelled) setPlan(null); });
        return () => { cancelled = true; };
    }, [open, lot, level, aqlMajor, aqlMinor]);

    const canSave = !!orderId && !!date && Number(lot) >= 1 && !saving;

    const save = async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setSaving(true);
        try {
            const req = {
                buyerOrderId: orderId, inspectionType: type, scheduledDate: date, lotSize: Number(lot),
                inspectionLevel: level, aqlMajor: Number(aqlMajor), aqlMinor: Number(aqlMinor),
                inspectorName: inspector.trim() || null,
            };
            const saved = editing
                ? await QcInspectionService.updateBooking(inst, editing.id, req)
                : await QcInspectionService.book(inst, req);
            toast.showSuccess(editing ? 'Inspection updated' : 'Inspection booked');
            onSaved(saved);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to save inspection');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-help-section="merchandising.qc.bookDialog">
            <DialogTitle>{editing ? 'Edit inspection booking' : 'Book inspection'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {!fixedOrderId && (
                        <SearchableSelect<IBuyerOrder>
                            data-help="merchandising.qc.field.order"
                            label="Order" options={orders} value={orderId} required disabled={!!editing}
                            onChange={id => setOrderId(id)}
                            getOptionLabel={o => `${o.orderNumber}${o.buyerPoNumber ? ` (PO ${o.buyerPoNumber})` : ''}`}
                        />
                    )}
                    <TextField data-help="merchandising.qc.field.type" select label="Inspection type" value={type}
                        onChange={e => setType(e.target.value as QcInspectionType)}>
                        {TYPES.map(t => <MenuItem key={t} value={t}>{QC_TYPE_LABELS[t]}</MenuItem>)}
                    </TextField>
                    <TextField data-help="merchandising.qc.field.date" type="date" label="Inspection date" value={date} required
                        InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
                    <TextField data-help="merchandising.qc.field.lot" type="number" label="Lot size (pieces)" value={lot} required
                        inputProps={{ min: 1 }} onChange={e => setLot(e.target.value)} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <TextField data-help="merchandising.qc.field.level" select label="Inspection level" value={level}
                            sx={{ flex: '1 1 140px', maxWidth: 220 }} onChange={e => setLevel(e.target.value as InspectionLevel)}>
                            {INSPECTION_LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                        </TextField>
                        <TextField data-help="merchandising.qc.field.aqlMajor" select label="AQL major" value={aqlMajor}
                            sx={{ flex: '1 1 120px', maxWidth: 200 }} onChange={e => setAqlMajor(e.target.value)}>
                            {SUPPORTED_AQLS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                        </TextField>
                        <TextField data-help="merchandising.qc.field.aqlMinor" select label="AQL minor" value={aqlMinor}
                            sx={{ flex: '1 1 120px', maxWidth: 200 }} onChange={e => setAqlMinor(e.target.value)}>
                            {SUPPORTED_AQLS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                        </TextField>
                    </Box>
                    <TextField data-help="merchandising.qc.field.inspector" label="Inspector" value={inspector}
                        inputProps={{ maxLength: 160 }} onChange={e => setInspector(e.target.value)} />
                    <PlanPreviewLine plan={plan} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={!canSave} data-help="merchandising.qc.save">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

interface ResultProps {
    inspection: IQcInspection | null;
    onClose: () => void;
    onSaved: (q: IQcInspection) => void;
}

export function QcResultDialog({ inspection, onClose, onSaved }: ResultProps) {
    const toast = useToast();
    const [date, setDate] = useState(toLocalIsoDate(new Date()));
    const [inspector, setInspector] = useState('');
    const [remarks, setRemarks] = useState('');
    const [defects, setDefects] = useState<IQcDefect[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!inspection) return;
        setDate(toLocalIsoDate(new Date()));
        setInspector(inspection.inspectorName ?? '');
        setRemarks('');
        setDefects([]);
    }, [inspection]);

    if (!inspection) return null;

    const setDefect = (i: number, patch: Partial<IQcDefect>) =>
        setDefects(ds => ds.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
    const valid = !!date && defects.every(d => d.defectName.trim() && d.defectCount >= 1);
    const sum = (sev: DefectSeverity) => defects.filter(d => d.severity === sev).reduce((a, d) => a + (d.defectCount || 0), 0);

    const save = async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setSaving(true);
        try {
            const saved = await QcInspectionService.recordResult(inst, inspection.id, {
                inspectedDate: date, inspectorName: inspector.trim() || null,
                defects: defects.map(d => ({ ...d, defectName: d.defectName.trim() })),
                resultRemarks: remarks.trim() || null,
            });
            toast.showSuccess(saved.status === 'PASSED' ? 'Result saved: passed' : 'Result saved: failed');
            onSaved(saved);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to record result');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md" data-help-section="merchandising.qc.resultDialog">
            <DialogTitle>Record result: {inspection.orderNumber}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Inspect {inspection.fullInspection ? 'every piece' : `${inspection.sampleSize} pieces`} (code {inspection.codeLetter}).
                        Critical: accept 0. Major: accept up to {inspection.majorAccept}. Minor: accept up to {inspection.minorAccept}.
                        The system decides pass or fail from the defects you list.
                    </Alert>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <TextField data-help="merchandising.qc.field.inspectedDate" type="date" label="Inspected on" value={date} required
                            sx={{ flex: '1 1 180px', maxWidth: 240 }} InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
                        <TextField data-help="merchandising.qc.field.inspector" label="Inspector" value={inspector}
                            sx={{ flex: '1 1 220px', maxWidth: 360 }} onChange={e => setInspector(e.target.value)} />
                    </Box>
                    <Typography variant="subtitle2" data-help="merchandising.qc.defects">Defects found</Typography>
                    {defects.map((d, i) => (
                        <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            <TextField label="Defect" size="small" value={d.defectName} sx={{ flex: '2 1 200px' }}
                                onChange={e => setDefect(i, { defectName: e.target.value })} />
                            <TextField select label="Severity" size="small" value={d.severity} sx={{ flex: '1 1 130px', maxWidth: 180 }}
                                onChange={e => setDefect(i, { severity: e.target.value as DefectSeverity })}>
                                {(Object.keys(SEVERITY_LABELS) as DefectSeverity[]).map(s =>
                                    <MenuItem key={s} value={s}>{SEVERITY_LABELS[s]}</MenuItem>)}
                            </TextField>
                            <TextField type="number" label="Count" size="small" value={d.defectCount} inputProps={{ min: 1 }}
                                sx={{ flex: '1 1 90px', maxWidth: 130 }}
                                onChange={e => setDefect(i, { defectCount: Number(e.target.value) })} />
                            <IconButton aria-label="Remove defect" onClick={() => setDefects(ds => ds.filter((_, idx) => idx !== i))}>
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}
                    <Box>
                        <Button startIcon={<AddIcon />} size="small" data-help="merchandising.qc.addDefect"
                            onClick={() => setDefects(ds => [...ds, { defectName: '', severity: 'MINOR', defectCount: 1 }])}>
                            Add defect
                        </Button>
                    </Box>
                    <Typography variant="body2" color="text.secondary" data-testid="qc-defect-totals">
                        Totals: critical {sum('CRITICAL')}, major {sum('MAJOR')}, minor {sum('MINOR')}
                    </Typography>
                    <TextField data-help="merchandising.qc.field.remarks" label="Remarks" multiline minRows={2} value={remarks}
                        inputProps={{ maxLength: 1000 }} onChange={e => setRemarks(e.target.value)} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={!valid || saving} data-help="merchandising.qc.saveResult">
                    Save result
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface ReinspectProps {
    inspection: IQcInspection | null;
    onClose: () => void;
    onSaved: (q: IQcInspection) => void;
}

export function QcReinspectDialog({ inspection, onClose, onSaved }: ReinspectProps) {
    const toast = useToast();
    const [date, setDate] = useState(toLocalIsoDate(new Date()));
    const [saving, setSaving] = useState(false);
    if (!inspection) return null;

    const save = async () => {
        const inst = AuthService.getSelectedInstitution();
        if (!inst) return;
        setSaving(true);
        try {
            const saved = await QcInspectionService.reinspect(inst, inspection.id, date, inspection.inspectorName ?? undefined);
            toast.showSuccess('Re-inspection booked');
            onSaved(saved);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to book re-inspection');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs" data-help-section="merchandising.qc.reinspectDialog">
            <DialogTitle>Book re-inspection</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Copies the lot size, level and AQL of the failed inspection of {inspection.orderNumber}.
                </Typography>
                <TextField data-help="merchandising.qc.field.date" type="date" label="Inspection date" value={date} fullWidth required
                    InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={!date || saving}>Book</Button>
            </DialogActions>
        </Dialog>
    );
}
